import { Head, Link, router } from '@inertiajs/react'
import { PageProps } from '@/types'
import AppLayout from '@/layouts/AppLayout'
import { Plus, Shield, AlertTriangle, ArrowLeft, Info } from 'lucide-react'
import DataTable, { Column } from '@/components/DataTable'

interface License {
  id: number
  agent_id: number
  agent_name?: string
  state_code: string
  license_number: string
  primary_residence_state: boolean
  status: string
  issued_at: string
  expires_at: string
  days_until_expiry: number | null
  expiring_soon: boolean
}

interface Agent {
  id: number
  full_name: string
  email: string
}

interface Summary {
  total: number
  active: number
  expiring_soon: number
  states: string[]
}

interface Props extends PageProps {
  licenses: License[]
  agent: Agent | null
  summary: Summary
  filter: string | null
  is_admin: boolean
}

export default function LicensesIndex({ licenses, agent, summary, filter, is_admin }: Props) {
  const statusColors: Record<string, string> = {
    active: 'bg-green-100 text-green-700',
    pending: 'bg-yellow-100 text-yellow-700',
    expired: 'bg-red-100 text-red-700',
    revoked: 'bg-gray-100 text-gray-700'
  }

  const columns: Column<License>[] = [
    ...(agent || !is_admin ? [] : [{
      key: 'agent_name' as keyof License,
      header: 'Agent',
      sortable: true,
      render: (license: License) => (
        <Link href={`/agents/${license.agent_id}`} className="font-medium text-chestnut-forest hover:text-chestnut-gold">
          {license.agent_name}
        </Link>
      )
    }]),
    {
      key: 'state_code',
      header: 'State',
      sortable: true,
      render: (license) => (
        <div className="flex items-center gap-2">
          <Shield className="h-4 w-4 text-chestnut-forest" />
          <span className="font-medium">{license.state_code}</span>
        </div>
      )
    },
    {
      key: 'license_number',
      header: 'License #',
      sortable: true,
      className: 'font-mono'
    },
    {
      key: 'primary_residence_state',
      header: 'Residence',
      sortable: true,
      render: (license) => (
        <span className={`px-2 py-1 text-xs font-medium rounded-full whitespace-nowrap ${
          license.primary_residence_state
            ? 'bg-chestnut-forest/10 text-chestnut-forest'
            : 'bg-gray-100 text-gray-600'
        }`}>
          {license.primary_residence_state ? 'Primary Residence State' : 'Non-Primary Residence State'}
        </span>
      )
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (license) => (
        <span className={`px-2 py-1 text-xs font-medium rounded-full whitespace-nowrap ${statusColors[license.status]}`}>
          {license.status}
        </span>
      )
    },
    {
      key: 'expires_at',
      header: 'Expires',
      sortable: true,
      render: (license) => (
        <div>
          <div className="text-sm text-chestnut-charcoal">
            {license.expires_at ? new Date(license.expires_at).toLocaleDateString() : '-'}
          </div>
          {license.expiring_soon && license.days_until_expiry !== null && (
            <div className="text-xs text-orange-600 font-medium">
              {license.days_until_expiry} days left
            </div>
          )}
        </div>
      )
    }
  ]

  const pageTitle = is_admin
    ? (agent ? `Licenses for ${agent.full_name}` : 'All Licenses')
    : 'My Licenses'

  const pageSubtitle = is_admin
    ? 'Manage state licenses and track expirations'
    : 'Track your state licenses and expirations'

  return (
    <AppLayout>
      <Head title={is_admin ? (agent ? `Licenses: ${agent.full_name}` : 'All Licenses') : 'My Licenses'} />

      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {agent && is_admin && (
              <Link href={`/agents/${agent.id}`} className="p-2 text-chestnut-charcoal/60 hover:text-chestnut-forest">
                <ArrowLeft className="h-5 w-5" />
              </Link>
            )}
            <div>
              <h1 className="text-3xl font-bold text-chestnut-charcoal font-heading">
                {pageTitle}
              </h1>
              <p className="text-chestnut-charcoal/70 mt-1">{pageSubtitle}</p>
            </div>
          </div>
          <Link
            href={agent ? `/agents/${agent.id}/licenses/new` : '/licenses/new'}
            className="btn-primary flex items-center gap-2"
          >
            <Plus className="h-5 w-5" />
            Add License
          </Link>
        </div>

        {!is_admin && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
            <Info className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-amber-900">
              Entering and tracking licenses is your sole responsibility. This functionality is only for your personal use and to help you track your licenses. Chestnut Capital and its affiliates do not take any responsibility for the accuracy of the data represented here. You are fully responsible for this process and tracking and managing with or without this system's assistance.
            </p>
          </div>
        )}

        {filter === 'expiring' && (
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 text-orange-600" />
              <div>
                <p className="font-medium text-orange-800">Showing licenses expiring within 30 days</p>
                <p className="text-sm text-orange-700">These licenses need attention soon.</p>
              </div>
            </div>
            <Link 
              href="/licenses" 
              className="text-sm text-orange-700 hover:text-orange-900 font-medium underline"
            >
              Show all licenses
            </Link>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="card border-l-4 border-l-chestnut-forest">
            <h3 className="text-sm font-medium text-chestnut-charcoal/60 uppercase tracking-wide">Total</h3>
            <p className="text-2xl font-bold text-chestnut-charcoal mt-1">{summary.total}</p>
          </div>
          <div className="card border-l-4 border-l-green-500">
            <h3 className="text-sm font-medium text-chestnut-charcoal/60 uppercase tracking-wide">Active</h3>
            <p className="text-2xl font-bold text-chestnut-charcoal mt-1">{summary.active}</p>
          </div>
          <div className="card border-l-4 border-l-orange-500">
            <h3 className="text-sm font-medium text-chestnut-charcoal/60 uppercase tracking-wide">Expiring Soon</h3>
            <p className="text-2xl font-bold text-chestnut-charcoal mt-1">{summary.expiring_soon}</p>
          </div>
          <div className="card border-l-4 border-l-blue-500">
            <h3 className="text-sm font-medium text-chestnut-charcoal/60 uppercase tracking-wide">States</h3>
            <p className="text-2xl font-bold text-chestnut-charcoal mt-1">{summary.states.length}</p>
          </div>
        </div>

        {summary.expiring_soon > 0 && !filter && (
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-orange-600" />
            <div>
              <p className="font-medium text-orange-800">
                {summary.expiring_soon} license{summary.expiring_soon > 1 ? 's' : ''} expiring within 30 days
              </p>
              <p className="text-sm text-orange-700">Review and renew to maintain compliance.</p>
            </div>
          </div>
        )}

        <div className="card p-6">
          <DataTable
            data={licenses}
            columns={columns}
            searchPlaceholder="Search licenses..."
            searchKeys={['state_code', 'license_number', 'agent_name']}
            pageSize={10}
            emptyMessage="No licenses on file."
            getRowKey={(license) => license.id}
            onRowClick={(license) => router.visit(`/licenses/${license.id}`)}
          />
        </div>
      </div>
    </AppLayout>
  )
}
